import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, Check, Trash2 } from "lucide-react";
import { API } from "@/lib/constants";

/**
 * Phase 5 Day 6 — Reference-set picker modal.
 *
 * Spec ref: § 2.3 + § 5.3 + Remi's Day 6 specifics (Feb 27 2026).
 *
 * Two surfaces:
 *  1. Current reference set (top) — operator can remove cards.
 *  2. Candidate list (bottom) — sorted by quality_score DESC by
 *     default, with a "Last 30 days" toggle that switches to a
 *     fresh-cards view (prevents the picker from being a backwards-
 *     looking surface when the operator's taste has shifted toward
 *     Phase 4-era cards).
 */
export default function ReferenceSetPicker({ open, onClose, workspaceId }) {
  const [refSet, setRefSet] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [last30Only, setLast30Only] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);  // card_id being added/removed

  const fetchAll = useCallback(async () => {
    if (!open || !workspaceId) return;
    setLoading(true);
    try {
      const refUrl = `${API}/v2/workspaces/${workspaceId}/critic/reference-set`;
      const candUrl =
        `${API}/v2/workspaces/${workspaceId}/critic/reference-set/candidates` +
        (last30Only ? "?limit=30&days=30" : "?limit=30");
      const [r1, r2] = await Promise.all([
        fetch(refUrl, { credentials: "include" }),
        fetch(candUrl, { credentials: "include" }),
      ]);
      setRefSet(r1.ok ? await r1.json() : null);
      setCandidates(r2.ok ? await r2.json() : []);
    } catch (e) {
      console.error("reference-set fetch failed", e);
      toast.error("Failed to load reference set");
    } finally {
      setLoading(false);
    }
  }, [open, workspaceId, last30Only]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const add = async (card_id) => {
    setBusy(card_id);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/critic/reference-set`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ card_id }),
      });
      if (!r.ok) throw new Error(`status ${r.status}`);
      toast.success("Added to reference set");
      await fetchAll();
    } catch (e) {
      console.error("add failed", e);
      toast.error("Could not add card");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (card_id) => {
    setBusy(card_id);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/critic/reference-set/${card_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`status ${r.status}`);
      toast.success("Removed from reference set");
      await fetchAll();
    } catch (e) {
      console.error("remove failed", e);
      toast.error("Could not remove card");
    } finally {
      setBusy(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50" onClick={onClose} data-testid="reference-set-picker-overlay" role="presentation">
      <div
        className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="reference-set-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reference-set-picker-title"
        aria-describedby="reference-set-picker-description"
      >
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <h2 id="reference-set-picker-title" className="text-sm font-semibold text-slate-900">Brand-presence reference set</h2>
            <p id="reference-set-picker-description" className="text-[11px] text-slate-500 mt-0.5">
              Lock 3-5 cards that represent the brand at its best. The critic compares every new card against this set.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="reference-set-picker-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading && (
          <div className="px-5 py-8 text-center text-xs text-slate-500" data-testid="reference-set-picker-loading">
            Loading…
          </div>
        )}

        {!loading && (
          <div className="overflow-y-auto px-5 py-4 space-y-5">
            {/* Current set */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[11px] uppercase font-semibold text-slate-500 tracking-wider">
                  Current set {refSet ? `(${refSet.size})` : ""}
                </h3>
                {refSet?.is_provisional && (
                  <span data-testid="reference-set-provisional-badge" className="text-[10px] rounded-full bg-amber-50 px-2 py-0.5 text-amber-700 font-medium">
                    Provisional (N&lt;5)
                  </span>
                )}
              </div>
              {refSet && refSet.cards.length > 0 ? (
                <ul className="space-y-1">
                  {refSet.cards.map((c) => (
                    <li key={c.card_id} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-xs">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-800 truncate">{c.title || c.card_id.slice(0, 8)}</div>
                        <div className="text-slate-500 text-[10px]">{c.editorial_register || "—"} · added {c.locked_at?.slice(0, 10)}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(c.card_id)}
                        disabled={busy === c.card_id}
                        data-testid={`reference-set-remove-${c.card_id}`}
                        className="ml-3 text-amber-700 hover:text-amber-900 disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded border border-dashed border-slate-300 px-3 py-4 text-center text-[11px] text-slate-500">
                  No reference cards yet. Pick 3-5 from the candidate list below.
                </div>
              )}
              {refSet?.set_hash && (
                <div className="mt-2 text-[10px] text-slate-400 font-mono">
                  set_hash: {refSet.set_hash.slice(0, 16)}…
                </div>
              )}
            </section>

            {/* Candidates */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[11px] uppercase font-semibold text-slate-500 tracking-wider">
                  Candidates · sorted by quality_score
                </h3>
                <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={last30Only}
                    onChange={(e) => setLast30Only(e.target.checked)}
                    data-testid="reference-set-last-30-toggle"
                    className="rounded border-slate-300"
                  />
                  Last 30 days only
                </label>
              </div>
              {candidates.length === 0 ? (
                <div className="rounded border border-dashed border-slate-300 px-3 py-4 text-center text-[11px] text-slate-500">
                  No candidates {last30Only ? "in the last 30 days" : ""}.
                </div>
              ) : (
                <ul className="space-y-1">
                  {candidates.map((c) => (
                    <li key={c.card_id} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-xs">
                      <div className="min-w-0 flex-1 flex items-center gap-3">
                        {c.published_image_url && (
                          <img src={c.published_image_url} alt={c.title || "reference"} className="w-10 h-10 rounded object-cover border border-slate-200" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-800 truncate">{c.title || c.card_id.slice(0, 8)}</div>
                          <div className="text-slate-500 text-[10px]">
                            {c.editorial_register || "—"} · q={c.quality_score?.toFixed?.(2) || "—"} · {c.published_at?.slice(0, 10) || "unpublished"}
                          </div>
                        </div>
                      </div>
                      {c.already_in_reference_set ? (
                        <span className="ml-3 text-[10px] text-emerald-700 inline-flex items-center gap-1">
                          <Check className="h-3 w-3" /> in set
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => add(c.card_id)}
                          disabled={busy === c.card_id}
                          data-testid={`reference-set-add-${c.card_id}`}
                          className="ml-3 rounded border border-blue-400 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-900 hover:bg-blue-100 disabled:opacity-50"
                        >
                          Add
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
